import React, { useRef, useState } from "react";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { AppDispatch } from "../../../../../../redux/store";
import { getBookingDetail } from "../../../ducks/actions";
import { LeaseTermForm, LeaseTermFormHandle } from "./components/LeaseTermForm";
import { ActionModal } from "../ActionModal";
import { PopupConfirm } from "../../../../../atoms/Popup";
import { useLeaseTermAmendments } from "../api/v1/useLeaseTermAmendments";
import { apiDateFormat } from "../../utils";
import PaymentScheduleAmendments, { PaymentScheduleFormHandle } from "./PaymentScheduleAmendments";
import RenewalTermForm from "./components/RenewalTermForm";

type LeaseActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type Step = "confirmation" | "finalization" | "Confirm";

export const RenewalTermAmendment = ({ isOpen, onClose }: LeaseActionModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const { leaseId = bookingDetails?.leaseId } = useParams();
  const paymentScheduleRef = useRef<PaymentScheduleFormHandle>(null);
  const { submit, loading } = useLeaseTermAmendments("booking/leaseAmendments");

  const renewalTermFormRef = useRef<LeaseTermFormHandle>(null);
  let isDepositPaid = bookingDetails?.leaseStatusHistory?.find((e) => e.status === "deposit_paid");
  const [step, setStep] = useState<Step>("confirmation");
  const [leaseTerm, setLeaseTerm] = useState(null);
  const [isPaymentScheduleAmendment, setIsPaymentScheduleAmendment] = useState(false);

  const resetForm = () => {
    setStep("confirmation");
    setLeaseTerm(null);
    setIsPaymentScheduleAmendment(false);
    // Delay resetting internal state so modal closes smoothly first
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleFormSubmit = async (body: any) => {
    // Helper: submit and handle common success behavior

    const saveLeaseTerms = async (payload: any) => {
      const response = await submit(payload);
      if (response?.code === 200) {
        leaseId && dispatch(getBookingDetail(leaseId));
        message.success("Renewal terms updated successfully");
        resetForm();
      }
    };

    if (isDepositPaid) {
      return PopupConfirm({
        title: "It will update the renewal terms.Are you sure?",
        cancelText: "No",
        okText: "Yes",
        onOk: async () => await saveLeaseTerms(body),
      });
    }

    if (!isDepositPaid && !isPaymentScheduleAmendment) {
      setLeaseTerm(body);
      return PopupConfirm({
        title:
          "Would you like to amend the payment schedule? If you choose No, your current changes will be saved as they are.",
        cancelText: "No",
        okText: "Yes",
        onOk: () => setIsPaymentScheduleAmendment(true),
        onCancel: async () => await saveLeaseTerms(body),
      });
    }

    if (isPaymentScheduleAmendment) {
      const payload = {
        ...leaseTerm,
        paymentSchedule: body.paymentSchedule,
      };

      return await saveLeaseTerms(payload);
    }
  };

  const handleOk = () => {
    if (step === "confirmation") {
      setStep("finalization");
    } else if (isPaymentScheduleAmendment) {
      paymentScheduleRef.current?.submitForm();
    } else {
      // Trigger child form submit via ref
      renewalTermFormRef.current?.submitForm();
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={resetForm}
      title="Renewal Term Amendments"
      steps={step === "confirmation" ? "Confirm" : "Submit"}
      handleOk={handleOk}
      resetForm={resetForm}
      confirmLoading={loading}
      width={step === "confirmation" ? 600 : 1400}
    >
      {isOpen && step === "confirmation" && (
        <div>
          <p>You are about to amend the Renewal term.</p>
          <p>This action may change the start date, end date, and the overall duration of the lease.</p>
          <p>These changes may also impact the payment schedule. Do you want to proceed?</p>
        </div>
      )}
      {isOpen && step === "finalization" && !isPaymentScheduleAmendment && (
        <RenewalTermForm ref={renewalTermFormRef} step={step} onSubmit={handleFormSubmit} />
      )}

      {isOpen && isPaymentScheduleAmendment && (
        <PaymentScheduleAmendments leaseTerms={leaseTerm} ref={paymentScheduleRef} onSubmit={handleFormSubmit} />
      )}
    </ActionModal>
  );
};
